import * as React from 'react'
import { useParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2, Presentation as PresentIcon, Printer } from 'lucide-react'
import type { ProposalDocumentDto } from '@lib/dto/proposal-document.dto'
import type { ProposalSelectionDto } from '@lib/dto/proposal-selection.dto'
import type { AcceptProposalInput } from '@lib/input/proposal-selection.input'
import { parseLinkToken } from '@lib/validators/link-token.validator'
import { proposalService } from '@/services/ProposalService'
import { ProposalDocument } from '@/proposal-document/ProposalDocument'
import { useChoosing, type SharedVersion } from '@/proposal-document/choosing'
import { ActivityProvider, useTrack } from '@/proposal-document/activity'
import { Presentation, enterFullScreen } from '@/proposal-document/Presentation'
import { PdfButton } from '@/proposal-document/PdfButton'
import { decodeVersion } from '@/schemas/proposalVersionLink'
import { useOrganizationBranding } from '@/hooks/useOrganization'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'

/**
 * A proposal as the client opens it, from the link in their email.
 *
 * Public, outside `ProtectedRoute`: the recipient has no account and is not
 * meant to need one, exactly as with the invite-accept page. The token in the
 * URL is the authorization.
 *
 * Deliberately not a copy of `ProposalDocumentPage`. That page is the internal
 * preview and carries internal chrome — a back link into the CRM, a draft
 * marker, a revision number. None of that means anything to a community
 * manager, and "revision 3" actively invites a question nobody wants asked at
 * the point of signing. What is shared is the document, a phone number and a
 * print button.
 */
export function SharedProposalPage() {
  const branding = useOrganizationBranding()
  const organizationName = branding.data?.name ?? 'the sender'
  const params = useParams<{ token: string }>()
  // A damaged link is not worth asking about: the answer can only be no.
  const token = parseLinkToken(params.token)

  const document = useQuery({
    // Not in the shared key factory: this is the only screen that reads it,
    // there is nothing to invalidate it against, and a token is not an id.
    queryKey: ['shared-proposal', token],
    queryFn: () => proposalService.getShared(token ?? ''),
    enabled: token !== null,
    retry: false,
  })

  if (token === null) {
    return <ProposalUnavailable organizationName={organizationName} />
  }

  if (document.isPending) {
    return (
      <div className="proposal-stage flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
      </div>
    )
  }

  if (document.isError) {
    return <ProposalUnavailable organizationName={organizationName} />
  }

  return (
    <ActivityProvider token={token}>
      <SharedProposal token={token} document={document.data} />
    </ActivityProvider>
  )
}

/**
 * One message for every failure, matching the API's single 410. A reader who
 * cannot open their proposal needs a person, not a reason — and a reason here
 * would tell somebody guessing at links which guess was close.
 */
function ProposalUnavailable({ organizationName }: { organizationName: string }) {
  return (
    <div className="proposal-stage flex items-center justify-center px-4">
      <div className="max-w-sm text-center">
        <p className="text-sm font-medium">This proposal is not available</p>
        <p className="mt-1 text-sm text-muted-text">
          The link may have expired or been replaced. Please contact{' '}
          {organizationName} for an up-to-date copy.
        </p>
      </div>
    </div>
  )
}

/** The plan a link arrived with, when somebody shared their version. */
function versionFromLink(): SharedVersion | undefined {
  if (typeof window === 'undefined') return undefined
  return decodeVersion(window.location.hash) ?? undefined
}

function SharedProposal({
  token,
  document,
}: {
  token: string
  document: ProposalDocumentDto
}) {
  const [accepted, setAccepted] = React.useState<ProposalSelectionDto | null>(null)
  const [acceptError, setAcceptError] = React.useState<string | null>(null)
  const [presenting, setPresenting] = React.useState(false)
  const track = useTrack()

  // Once per page load: the salesperson's "they opened it", and a print.
  React.useEffect(() => {
    track({ kind: 'OPENED' })
    const onPrint = (): void => {
      track({ kind: 'PRINTED' })
    }
    window.addEventListener('beforeprint', onPrint)
    return () => {
      window.removeEventListener('beforeprint', onPrint)
    }
  }, [track])
  const choosing = useChoosing(document, {
    initial: versionFromLink(),
    isLocked: accepted !== null,
  })

  const accept = useMutation({
    mutationFn: (input: AcceptProposalInput) =>
      proposalService.acceptShared(token, input),
  })

  async function handleAccept(): Promise<void> {
    setAcceptError(null)
    const { selection, state } = choosing
    try {
      setAccepted(
        await accept.mutateAsync({
          ...selection,
          // Recorded beside the choices; the price is still worked out on
          // the server from the choices alone.
          plan: state.plan,
          budgetAmount: null,
        }),
      )
    } catch (caught) {
      setAcceptError(
        isApiError(caught)
          ? (caught.body.message ?? 'That could not be accepted.')
          : toErrorMessage(caught),
      )
    }
  }

  const cover = document.blocks.find((block) => block.type === 'cover')
  const phone = cover?.type === 'cover' ? cover.phone : null
  const shareUrl =
    typeof window === 'undefined'
      ? null
      : `${window.location.origin}${window.location.pathname}`

  return (
    <div className="proposal-stage">
      <div className="proposal-topbar print:hidden">
        <span className="proposal-topbar-name">
          {cover?.type === 'cover' ? cover.preparedByOrganization : 'Proposal'}
        </span>
        <span className="proposal-topbar-actions">
          {phone === null ? null : (
            <a className="proposal-topbar-phone" href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>
              {phone}
            </a>
          )}
          <button
            type="button"
            className="proposal-topbar-button proposal-topbar-button--present"
            onClick={() => {
              enterFullScreen()
              setPresenting(true)
              track({ kind: 'PRESENTED' })
            }}
          >
            <PresentIcon className="h-3.5 w-3.5" aria-hidden />
            Present
          </button>
          <PdfButton
            load={() => proposalService.getSharedPdf(token)}
            onDownload={() => {
              track({ kind: 'PRINTED', detail: 'pdf' })
            }}
          />
          <button
            type="button"
            className="proposal-topbar-button proposal-topbar-button--print"
            onClick={() => {
              window.print()
            }}
          >
            <Printer className="h-3.5 w-3.5" aria-hidden />
            Print
          </button>
        </span>
      </div>
      <ProposalDocument
        document={document}
        choosing={choosing}
        photoSource={{ kind: 'shared', token, forPrint: false }}
        shareUrl={shareUrl}
        isPresenting={presenting}
        accept={{
          token,
          accepted,
          isPending: accept.isPending,
          error: acceptError,
          onAccept: () => {
            void handleAccept()
          },
        }}
      />
      {presenting ? (
        <Presentation
          document={document}
          photoSource={{ kind: 'shared', token, forPrint: false }}
          shareUrl={shareUrl}
          onClose={() => {
            setPresenting(false)
          }}
        />
      ) : null}
    </div>
  )
}
